import { Box, Flex } from '@chakra-ui/react';
import { ChevronDown, ChevronUp, CompaRatio } from '@mesh.ai/rabbit';
import { useQuery } from 'components/hooks/useQuery';
import { format, formatDistanceToNowStrict } from 'date-fns';

import { NeedAttention, RoleIcon } from 'assets/assetsV1/iconComponents';

import { Avatar } from 'uiToolkitV1/Avatar';
import { DatePicker } from 'uiToolkitV1/DatePicker';
import { SalaryPercentageInput } from 'uiToolkitV1/SalaryPercentageInput';
import { TextBodyMedium, TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

export const EmployeeCellRenderer = ({
  value,
  data,
  node,
}: {
  value:
    | {
        displayName?: string;
        profilePhoto?: string;
        mainRole?: string;
        otherRoles?: string;
      }
    | null
    | undefined;
  data: any;
  node: any;
}) => {
  const { hierarchy, FLAGGED_PROPOSAL } = data ?? {};
  const { activeTabId } = useQuery();

  const isProposeTab = activeTabId === 'propose';

  const isSubRole = hierarchy?.length > 1;
  const totalOtherRoles = value?.otherRoles?.length;

  const handleViewOtherRolesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (node && totalOtherRoles) {
      node.setExpanded(!node.expanded);
    }
  };

  if (isSubRole) {
    return (
      <Flex alignItems="center" gap="8px" h="100%">
        <RoleIcon />
        <TextBodyMedium>{value?.mainRole}</TextBodyMedium>
      </Flex>
    );
  }

  if (!value || !value.displayName) {
    return (
      <Flex alignItems="center" gap="8px" h="100%">
        <Avatar name="-" src={undefined} size={24} />
        <Flex flexDirection="column">
          <TextBodyMedium>-</TextBodyMedium>
          <TextLabel color="text.g2">-</TextLabel>
        </Flex>
      </Flex>
    );
  }
  const { displayName, profilePhoto, mainRole } = value;

  return (
    <Flex alignItems="center" gap="8px" h="100%">
      {FLAGGED_PROPOSAL && !isProposeTab ? <NeedAttention color="button.red.default" /> : <Box h="16px" minW="16px" />}
      <Avatar name={displayName} src={profilePhoto} size={24} />
      <Flex flexDirection="column" sx={{ button: { padding: '0px !important', height: '20px !important' } }}>
        <TextBodyMedium>{displayName}</TextBodyMedium>
        <TextLabel color="text.g2">{mainRole || '-'}</TextLabel>
        {totalOtherRoles > 0 ? (
          <Flex
            alignItems="center"
            gap="4px"
            cursor="pointer"
            onClick={handleViewOtherRolesClick}
            _hover={{ opacity: 0.8 }}
            data-interactive="true"
          >
            <TextLabelSmall color={node?.expanded ? '#004F67' : '#0D819F'} bold>
              {node?.expanded ? 'Hide additional rates' : `Show additional rates`}
            </TextLabelSmall>
            {node?.expanded ? (
              <ChevronUp width="24px" height="24px" color="#004F67" />
            ) : (
              <ChevronDown width="24px" height="24px" color="#0D819F" />
            )}
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  );
};

export const RATING_TEXT_AND_COLORS = {
  MEETS_EXPECTATIONS: {
    text: 'Meets Expectations',
    color: '#8C6A00',
    background: '#FFF6D3',
  },
  EXCEEDS_EXPECTATIONS: {
    text: 'Exceeds Expectations',
    color: '#44812F',
    background: '#EFFFEA',
  },
  OUTSTANDING: {
    text: 'Outstanding',
    color: '#004F67',
    background: '#E6F1FD',
  },
  NEEDS_IMPROVEMENT: {
    text: 'Needs Improvement',
    color: '#C62C02',
    background: '#FFF1E4',
  },
};

export const RatingCellRenderer = ({ value, data }: { value: string; data: any }) => {
  const { hierarchy } = data ?? {};
  const isSubRole = hierarchy?.length > 1;
  if (isSubRole) {
    return null;
  }
  return (
    <Flex alignItems="center" h="100%">
      <Box p="4px 8px" w="fit-content" borderRadius="4px" backgroundColor={RATING_TEXT_AND_COLORS[value]?.background}>
        <TextLabel bold color={RATING_TEXT_AND_COLORS[value]?.color}>
          {RATING_TEXT_AND_COLORS[value]?.text}
        </TextLabel>
      </Box>
    </Flex>
  );
};

export const CHURN_TEXT_AND_COLORS = {
  HIGH: {
    text: 'High',
    color: '#C62C02',
    background: '#FFF1E4',
  },
  MEDIUM: {
    text: 'Medium',
    color: '#8C6A00',
    background: '#FFF6D3',
  },
  LOW: {
    text: 'Low',
    color: '#44812F',
    background: '#EFFFEA',
  },
};
export const ChurnLikelihoodCellRenderer = ({ value, data }: { value: string; data: any }) => {
  const { hierarchy } = data;
  const isSubRole = hierarchy?.length > 1;
  if (isSubRole) {
    return null;
  }
  return (
    <Flex alignItems="center" h="100%">
      <Box p="4px 8px" w="fit-content" borderRadius="4px" backgroundColor={CHURN_TEXT_AND_COLORS[value]?.background}>
        <TextLabel bold color={CHURN_TEXT_AND_COLORS[value]?.color}>
          {CHURN_TEXT_AND_COLORS[value]?.text}
        </TextLabel>
      </Box>
    </Flex>
  );
};

export const WAITING_ON_STATUS_MAP = {
  IN_PROGRESS: 'In progress',
  REVIEW_PENDING: 'Review pending',
  AWAITING_APPROVAL: 'Awaiting approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const WaitingOnCellRenderer = ({
  value: { status, employee },
  data,
}: {
  value: {
    status: string;
    employee: {
      displayName: string;
      profilePhoto: string;
    };
  };
  data: any;
}) => {
  const { hierarchy } = data;
  const isSubRole = hierarchy?.length > 1;
  if (isSubRole) {
    return null;
  }
  return (
    <Flex alignItems="center" gap="8px" h="100%">
      <Avatar name={employee.displayName} src={employee.profilePhoto} size={24} />
      <Flex flexDirection="column">
        <TextLabelSmall>{WAITING_ON_STATUS_MAP[status] ?? status}</TextLabelSmall>
        <TextBodyMedium>{employee.displayName}</TextBodyMedium>
      </Flex>
    </Flex>
  );
};

export const COMPA_RATIO_CELL_RENDERER = ({ value }: { value: number }) => (
  <Flex alignItems="center" h="100%">
    <CompaRatio value={value} />
    <TextBodyMedium bold ml="8px">
      {(value * 100).toFixed(0)}%
    </TextBodyMedium>
  </Flex>
);

export const BaseRateCellRenderer = ({ value }: { value: { symbol: string; value: number; currency: string } }) => (
  <Flex alignItems="center" h="100%" px="12px" py="8px">
    <TextBodyMedium>
      {value.symbol}
      {value.value}/hr
    </TextBodyMedium>
  </Flex>
);

export const RecommendationCellRenderer = ({
  value,
}: {
  value: { symbol: string; value: number; currency: string };
}) => (
  <Flex alignItems="center" h="100%">
    <TextBodyMedium>
      {value.symbol}
      {value.value}/hr
    </TextBodyMedium>
  </Flex>
);

export const ProposedBaseSalaryCellRenderer = ({
  value,
  data,
}: {
  value: {
    symbol: string;
    value: number;
    currency: string;
    guidanceValue?: { minPercentage: number; maxPercentage: number };
  };
  data: {
    BASE_RATE: {
      symbol: string;
      value: number;
      currency: string;
    };
  };
}) => {
  const baseRate = data?.BASE_RATE?.value;

  return (
    <SalaryPercentageInput
      baseValue={baseRate}
      initialValue={value.value}
      currencySymbol={value.symbol}
      unitSuffix="/hr"
      guidelineMin={value.guidanceValue?.minPercentage}
      guidelineMax={value.guidanceValue?.maxPercentage}
      percentageInputWidth="100px"
    />
  );
};

export const EffectiveDateCellRenderer = ({ value, data }: { value: string; data: any }) => {
  const { hierarchy } = data;
  const isSubRole = hierarchy?.length > 1;
  if (isSubRole) {
    return null;
  }
  return (
    <Flex alignItems="center" h="100%">
      <DatePicker
        onChange={() => {}} // TODO: Implement date change handler
        selectedDate={new Date(value)}
        popperPlacement="bottom-start"
        portalId="portal-root"
        // minDate={minDate}
        // maxDate={maxDate}
      />
    </Flex>
  );
};

export const LastDateOfChangeCellRenderer = ({ value }: { value: string }) => (
  <Flex justifyContent="center" flexDir="column" h="100%">
    <TextBodyMedium>
      {formatDistanceToNowStrict(new Date(value), {
        addSuffix: true,
      })}
    </TextBodyMedium>
    <TextLabel color="text.g2">{format(new Date(value), 'MMM dd, yyyy')}</TextLabel>
  </Flex>
);

export const NotesCellRenderer = ({ value }: { value: string }) => (
  <Flex h="100%" alignItems="center">
    <TextBodyMedium isCharCountTruncate truncateCharCount={35}>
      {value}
    </TextBodyMedium>
  </Flex>
);

export const CompensationCycleCellRenderer = ({ value, data }) => {
  const { hierarchy } = data ?? {};
  const isSubRole = hierarchy?.length > 1;
  if (isSubRole) {
    return null;
  }
  return (
    <Flex alignItems="center" h="100%">
      <TextBodyMedium>{value || '-'}</TextBodyMedium>
    </Flex>
  );
};
